const PAGE_WRAPPER_SELECTOR = '.page-wrapper';
const SQUEEZE_QUERY = window.matchMedia('(min-width: 990px)');

/**
 * Returns the current page scroll container.
 * In the squeeze layout (desktop ≥990px), `.page-wrapper` is the scroll container instead of
 * `document.scrollingElement`. On mobile, the document root scrolls natively so the address bar
 * can hide/show.
 *
 * @returns {Element} The scroll container element
 */
function getScrollContainer() {
  if (SQUEEZE_QUERY.matches) {
    return document.querySelector(PAGE_WRAPPER_SELECTOR) ?? document.scrollingElement ?? document.documentElement;
  }
  return document.scrollingElement ?? document.documentElement;
}

/**
 * Returns the current scroll position of the page scroll container.
 *
 * @returns {number} The scrollTop value
 */
function getScrollTop() {
  return getScrollContainer().scrollTop;
}

/**
 * Scrolls the page scroll container to the specified position.
 *
 * @param {ScrollToOptions} options - The scroll options (top, left, behavior)
 */
function scrollTo(options) {
  getScrollContainer().scrollTo(options);
}

/**
 * Manual scroll restoration for `.page-wrapper` as scroll container.
 *
 * On desktop (≥990px), the browser restores scroll on `document.scrollingElement`, which
 * has `overflow: hidden` in the squeeze layout and can never scroll. We disable native
 * restoration and handle it via `history.state` instead.
 *
 * On mobile (<990px), `document.scrollingElement` IS the scroll container, so native
 * restoration works perfectly — leave it enabled to avoid a one-frame flash from rAF.
 *
 * Save: `pagehide` — fires exactly once per navigation, captures the exact scroll position
 * at the moment the user leaves. More accurate than debounced scroll (which can be stale
 * if the user scrolls and immediately clicks a link).
 *
 * Restore: unconditional `pageshow` — Horizon uses cross-document view transitions, so
 * `pageshow` fires on every navigation (both bfcache and fresh loads). `popstate` is not
 * used because it doesn't fire for cross-document back navigation.
 */
if (SQUEEZE_QUERY.matches) {
  history.scrollRestoration = 'manual';
}

SQUEEZE_QUERY.addEventListener('change', () => {
  history.scrollRestoration = SQUEEZE_QUERY.matches ? 'manual' : 'auto';
});

/**
 * Saves the current scroll position into the current history entry.
 */
function saveScrollPosition() {
  try {
    const currentState = typeof history.state === 'object' && history.state !== null ? history.state : {};
    history.replaceState({ ...currentState, scrollTop: getScrollContainer().scrollTop }, '');
  } catch (_) {
    // replaceState can throw if the state object exceeds the browser's size limit
  }
}

window.addEventListener('pagehide', saveScrollPosition);

/**
 * Restores a saved scroll position onto the current scroll container.
 *
 * @param {number} savedScrollTop
 */
/**
 * Restores a saved scroll position after a back/forward navigation.
 *
 * Re-applies across frames because the container can be short for a few frames
 * while images and fonts settle (a single rAF often clamps to ~0 and never
 * recovers). The target is also capped to the bottom of the product results, so a
 * shorter re-render — e.g. "Load more" rendering a single page on back — lands on
 * the last product row instead of continuing on into the footer.
 *
 * @param {number} savedScrollTop
 */
function restoreSavedScrollTop(savedScrollTop) {
  if (!Number.isFinite(savedScrollTop) || savedScrollTop < 0) return;

  const start = performance.now();

  const apply = () => {
    const container = getScrollContainer();

    let target = savedScrollTop;
    const results = document.getElementById('ResultsList');
    if (results) {
      const containerTop = container === document.scrollingElement ? 0 : container.getBoundingClientRect().top;
      const resultsBottom = container.scrollTop + results.getBoundingClientRect().bottom - containerTop;
      target = Math.min(savedScrollTop, Math.max(0, resultsBottom - container.clientHeight));
    }

    // Use 'instant' to override CSS scroll-behavior: smooth on .page-wrapper.
    container.scrollTo({ top: target, behavior: 'instant' });

    const reached = Math.abs(container.scrollTop - target) < 2;
    if (!reached && performance.now() - start < 1000) {
      requestAnimationFrame(apply);
    }
  };

  requestAnimationFrame(apply);
}

let scrollRestored = false;

/**
 * Restores from the current history entry's saved scrollTop, if one exists.
 */
function runScrollRestore() {
  const scrollTop = history.state?.scrollTop;
  if (scrollTop == null) return;
  scrollRestored = true;
  restoreSavedScrollTop(scrollTop);
}

// On a fresh (non-bfcache) back/forward load, `pageshow` only fires after `load`
// — i.e. after every product image has finished — which makes the jump to the
// saved position feel slow. Restore as soon as the DOM is parsed instead: product
// cards reserve their height via aspect-ratio, so the saved position is reachable
// well before images finish, and the retry loop keeps it accurate as the rest
// settles.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', runScrollRestore, { once: true });
} else {
  runScrollRestore();
}

window.addEventListener('pageshow', (event) => {
  // bfcache restores don't re-parse the DOM, so the early restore above never ran
  // for them — handle those here. On a fresh load the early restore already
  // positioned us, so don't fire again (it would yank a user who has since
  // started scrolling while images were still loading).
  if (!event.persisted && scrollRestored) return;
  runScrollRestore();
});

/**
 * Same-document scroll restoration for pushState navigation.
 *
 * `pagehide`/`pageshow` only fire for cross-document navigations. For same-document
 * pushState navigation (filters, pagination), we patch pushState to auto-save scroll
 * position before each new entry, and restore on popstate (back/forward).
 */
const originalPushState = history.pushState.bind(history);
history.pushState = function (state, title, url) {
  saveScrollPosition();
  originalPushState(state, title, url);
};

window.addEventListener('popstate', () => {
  const scrollTop = history.state?.scrollTop;
  if (scrollTop == null) return;
  restoreSavedScrollTop(scrollTop);
});

/**
 * Returns the appropriate target for listening to scroll events.
 * On desktop (≥990px), `.page-wrapper` emits scroll events directly.
 * On mobile, the document root scrolls natively and scroll events bubble to `document`.
 *
 * @returns {EventTarget} The target to call addEventListener('scroll', ...) on
 */
function getScrollEventTarget() {
  if (SQUEEZE_QUERY.matches) {
    return document.querySelector(PAGE_WRAPPER_SELECTOR) ?? document;
  }
  return document;
}

/**
 * Returns the appropriate root for an IntersectionObserver monitoring the scroll container.
 * On desktop (≥990px), `.page-wrapper` must be set as the explicit root.
 * On mobile, `null` uses the viewport root (the IntersectionObserver default).
 *
 * @returns {Element | null} The root option for IntersectionObserver
 */
function getIntersectionRoot() {
  if (SQUEEZE_QUERY.matches) {
    return document.querySelector(PAGE_WRAPPER_SELECTOR) ?? null;
  }
  return null;
}

export {
  getScrollContainer,
  getScrollTop,
  scrollTo,
  getScrollEventTarget,
  getIntersectionRoot,
  SQUEEZE_QUERY as scrollContainerMediaQuery,
};
